import React, { useState } from 'react';
import { Typography } from '@/lib/typography';
import { SHARED_UI_LABELS } from '@/config/task-display-config';

export default function StartupEditForm({ startup, onSave, onCancel, saving }) {
    const [formData, setFormData] = useState({
        name: startup.name || '',
        stage: startup.stage || '',
        status: startup.status || '',
        round_size: startup.round_size || '',
        location: startup.location || '',
    });

    const [errors, setErrors] = useState({});

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Startup name is required';
        if (!formData.stage.trim()) newErrors.stage = 'Stage is required';
        if (!formData.status.trim()) newErrors.status = 'Status is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            // Convert round_size to number if provided
            const dataToSave = {
                ...formData,
                round_size: formData.round_size ? parseFloat(formData.round_size) : null,
            };
            onSave(dataToSave);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };


    const stages = ['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C+'];
    const statuses = ['Unassigned', 'Warm', 'Mandated', 'In-Market', 'Closed', 'Rejected'];


    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-slate-700 mb-1">
                    Startup Name <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.name && <p className="text-red-500 mt-1">{errors.name}</p>}
            </div>

            {/* The sector is NOT typed here (CEO decision relayed in the 3 Oct 2026 sector-cleanup brief). The taxonomy code is the only
                official sector tag (ruling 241) and is written by the tagging path from the frozen list in
                sia_config.json; a free-text box collected a sector the record never stored. */}

            <div>
                <label className="block text-slate-700 mb-1">
                    Stage <span className="text-red-500">*</span>
                </label>
                <select
                    value={formData.stage}
                    onChange={(e) => handleChange('stage', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg ${errors.stage ? 'border-red-500' : 'border-gray-300'}`}
                >
                    <option value="">Select Stage</option>
                    {stages.map(stage => (
                        <option key={stage} value={stage}>{stage}</option>
                    ))}
                </select>
                {errors.stage && <p className="text-red-500 mt-1">{errors.stage}</p>}
            </div>

            <div>
                <label className="block text-slate-700 mb-1">
                    Status <span className="text-red-500">*</span>
                </label>
                <select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg ${errors.status ? 'border-red-500' : 'border-gray-300'}`}
                >
                    <option value="">Select Status</option>
                    {statuses.map(status => (
                        <option key={status} value={status}>{status}</option>
                    ))}
                </select>
                {errors.status && <p className="text-red-500 mt-1">{errors.status}</p>}
            </div>

            <div>
                <label className="block text-slate-700 mb-1">Round Size (USD)</label>
                <input
                    type="number"
                    value={formData.round_size}
                    onChange={(e) => handleChange('round_size', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., 1000000" // noscan
                />
            </div>

            <div>
                <label className="block text-slate-700 mb-1">Location</label>
                <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Bangalore, San Francisco" // noscan
                />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </form>
    );
}
